import { FilterBar } from "@bcc/components/finance/filter-bar";
import { InvoiceDetailDrawer } from "@bcc/components/finance/invoice-detail-drawer";
import { InvoiceTable } from "@bcc/components/finance/invoice-table";
import { Button } from "@bcc/components/ui/button";
import { useAuth } from "@bcc/contexts/auth-context";
import { useToast } from "@bcc/hooks/use-toast";
import type { Invoice, InvoiceFilters } from "@bcc/lib/types/invoice";
import { FileSpreadsheet } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  setFinanceInvoicesViewExportCsvButtonTestId,
  setFinanceInvoicesViewInvoiceCountTestId,
  setFinanceInvoicesViewTestId,
  setFinanceInvoicesViewTitleTestId,
} from "./finance-invoices-view.utils";

interface FinanceInvoicesViewProps {
  initialFilters: InvoiceFilters;
  testId?: string;
}

// Subcomponents
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-64 animate-pulse rounded bg-muted" />
      <div className="h-32 animate-pulse rounded-lg bg-muted" />
      <div className="h-96 animate-pulse rounded-lg bg-muted" />
    </div>
  );
}

interface InvoicesHeaderProps {
  invoiceCount: number;
  canExport: boolean;
  onExportCSV: () => void;
}

function InvoicesHeader({
  invoiceCount,
  canExport,
  onExportCSV,
}: InvoicesHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1
          data-testid={setFinanceInvoicesViewTitleTestId()}
          className="text-2xl font-semibold tracking-tight text-balance"
        >
          {t("views.financeInvoices.title")}
        </h1>
        <p
          data-testid={setFinanceInvoicesViewInvoiceCountTestId()}
          className="mt-1 text-sm text-muted-foreground"
        >
          {t("views.financeInvoices.invoicesFound", { count: invoiceCount })}
        </p>
      </div>
      {canExport && (
        <div className="flex gap-2">
          <Button
            data-testid={setFinanceInvoicesViewExportCsvButtonTestId()}
            onClick={onExportCSV}
            variant="outline"
            className="gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {t("views.financeInvoices.exportCsv")}
          </Button>
        </div>
      )}
    </div>
  );
}

export function FinanceInvoicesView({
  initialFilters,
  testId,
}: FinanceInvoicesViewProps) {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filters, setFilters] = useState<InvoiceFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast } = useToast();
  const { roaster } = useAuth();
  // Mock finance role since auth context might not have it
  const userFinanceRole = "finance-ops";
  const canExport = userFinanceRole === "finance-ops";

  useEffect(() => {
    const savedFilters = localStorage.getItem("invoice-filters");
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
  }, []);

  useEffect(() => {
    const fetchInvoices = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.status) params.set("status", filters.status.join(","));
        if (filters.country) params.set("country", filters.country.join(","));
        if (filters.crossBorderOnly) params.set("crossBorder", "true");
        if (filters.dateType) params.set("dateType", filters.dateType);
        if (filters.dateFrom) params.set("from", filters.dateFrom);
        if (filters.dateTo) params.set("to", filters.dateTo);
        if (filters.query) params.set("q", filters.query);

        const response = await fetch(`/api/invoices?${params.toString()}`);
        const data = await response.json();
        setInvoices(data.invoices);

        localStorage.setItem("invoice-filters", JSON.stringify(filters));
      } catch (error) {
        console.error("Failed to fetch invoices:", error);
        toast({
          title: t("common.error"),
          description: t("views.financeInvoices.errors.loadFailed"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [filters, toast]);

  const handleFiltersChange = (newFilters: InvoiceFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (query: string) => {
    setFilters({ ...filters, query: query || undefined });
  };

  const handleExportCSV = async () => {
    try {
      const response = await fetch("/api/export", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ format: "csv", filters }),
      });
      const data = await response.json();
      toast({
        title: t("views.financeInvoices.exportStarted"),
        description: t("views.financeInvoices.exportingInvoices", {
          count: invoices.length,
        }),
      });
      console.log("[v0] CSV Export:", data);
    } catch (error) {
      console.error("Export failed:", error);
      toast({
        title: t("views.financeInvoices.exportFailed"),
        description: t("views.financeInvoices.errors.exportFailed"),
        variant: "destructive",
      });
    }
  };

  const handleInvoiceClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDrawerOpen(true);
  };

  if (isLoading && invoices.length === 0) {
    return <LoadingSkeleton />;
  }

  return (
    <div
      data-testid={testId ?? setFinanceInvoicesViewTestId("container")}
      className="space-y-6"
    >
      <InvoicesHeader
        invoiceCount={invoices.length}
        canExport={canExport}
        onExportCSV={handleExportCSV}
      />

      {/* Filters */}
      <FilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
      />

      {/* Table */}
      <InvoiceTable invoices={invoices} onInvoiceClick={handleInvoiceClick} />

      {/* Detail Drawer */}
      <InvoiceDetailDrawer
        invoice={selectedInvoice}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        showAuditTab={userFinanceRole === "finance-ops"}
      />
    </div>
  );
}
